import { HttpClient } from '@angular/common/http';
import {
	Component,
	computed,
	EventEmitter,
	Input,
	input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	signal,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '@environment/environment';
import { TranslocoService } from '@jsverse/transloco';
import { ViewportService } from '@services/geo/viewport.service';
import { ValidationService } from '@services/ui/validation.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { InputComponent } from '@shared/components/input/input.component';
import { PasswordComponent } from '@shared/components/password/password.component';
import { SharedModule } from '@shared/modules/shared.module';
import { ForgotPasswordModalComponent } from './forgot-password-modal/forgot-password-modal.component';

interface DevLoginUser {
	label: string;
	email: string;
	role?: string;
}

@Component({
	selector: 'app-auth-form',
	imports: [
		SharedModule,
		ButtonComponent,
		InputComponent,
		PasswordComponent,
		CheckboxComponent,
		ForgotPasswordModalComponent,
	],
	templateUrl: './auth-form.component.html',
	styleUrl: './auth-form.component.scss',
})
export class AuthFormComponent implements OnChanges, OnInit {
	@Input() isRegister!: 1 | 2 | 3 | 4; // 1 = Login, 2 = Reset Password, 3 = Reset Password Confirm, 4 = MFA Verification
	@Output() formSubmit = new EventEmitter<{
		email?: string;
		password?: string;
		newPassword?: string;
		confirmPassword?: string;
		mfaCode?: string;
		trustDevice?: boolean;
		devLogin?: boolean;
	}>();
	@Output() cancelMFA = new EventEmitter<void>();
	@Input() errorMessage: string | null = null;
	@Input() showSuccessState = false; // For reset-password-confirm success state
	@Input() loading = false; // Loading state for submit button

	// UI State
	showForgotPasswordModal = false;
	isMobileView = false;
	inputSize: 'large' | 'medium' | 'small' = 'large'; // Default to large

	// MFA State
	mfaCode: string[] = ['', '', '', '', '', ''];
	trustDevice = false;

	// Dev login (localhost/tailnet only)
	devLoginMenuOpen = false;
	devLoginUsers: DevLoginUser[] = [];
	devLoginAvailable = false;

	// Form
	authForm: FormGroup;

	// Navigation
	anchorUrl = input('');
	close = signal('/login');

	// Branding
	logo = computed(() => 'assets/layout/images/branding/eris-text.png');

	constructor(
		private fb: FormBuilder,
		private validationService: ValidationService,
		private viewportService: ViewportService,
		private router: Router,
		private translocoService: TranslocoService,
		private http: HttpClient,
	) {
		this.authForm = this.fb.group({});
	}

	/**
	 * Returns the form fields based on the form type (login/reset)
	 */
	get formFields(): string[] {
		switch (this.isRegister) {
			case 1: // Login
				return ['email', 'password'];
			case 2: // Reset Password
				return ['email'];
			case 3: // Reset Password Confirm
				return ['newPassword', 'confirmPassword'];
			case 4: // MFA Verification
				return []; // No traditional form fields for MFA
			default:
				return [];
		}
	}

	/**
	 * Checks if MFA code is complete (all 6 digits filled)
	 */
	get isMfaCodeComplete(): boolean {
		return this.mfaCode.every((digit) => digit.length === 1);
	}

	ngOnInit(): void {
		this.updateFormValidations();
		this.checkViewportSize();
		this.loadDevLoginUsers();

		// Only add event listener in browser environment
		if (typeof window !== 'undefined') {
			window.addEventListener('resize', () => this.checkViewportSize());
		}
	}

	ngOnChanges(changes: { isRegister?: SimpleChanges }): void {
		if (changes.isRegister) {
			this.updateFormValidations();
		}
	}

	get showDevLogin(): boolean {
		// Hard-gated to local/private hosts: never renders on a deployed domain.
		return this.isRegister === 1 && this.isLocalHost() && this.devLoginAvailable;
	}

	toggleDevLoginMenu(): void {
		if (this.loading) return;
		this.devLoginMenuOpen = !this.devLoginMenuOpen;
	}

	onDevLogin(user: DevLoginUser): void {
		if (this.loading) return;
		this.devLoginMenuOpen = false;
		this.formSubmit.emit({
			email: user.email,
			devLogin: true,
		});
	}

	private loadDevLoginUsers(): void {
		if (!this.isLocalHost()) return;

		// Source of truth: the backend lists the users of the CURRENT DB schema
		// (respects DATABASE_SCHEMA), so the dropdown always matches whatever
		// schema the API points to and any users created since. If the endpoint
		// fails (older backend / offline), fall back to the static JSON files:
		// the gitignored local override first, then the committed demo default.
		this.http.get<DevLoginUser[]>(`${environment.pathBackend}/auth/dev-users`).subscribe({
			next: (users) => {
				if (users?.length) this.setDevLoginUsers(users);
				else this.loadDevLoginUsersFromAssets();
			},
			error: () => this.loadDevLoginUsersFromAssets(),
		});
	}

	private loadDevLoginUsersFromAssets(): void {
		this.http.get<DevLoginUser[]>('assets/dev-login-users.local.json').subscribe({
			next: (users) => this.setDevLoginUsers(users),
			error: () => {
				this.http.get<DevLoginUser[]>('assets/dev-login-users.json').subscribe({
					next: (users) => this.setDevLoginUsers(users),
					error: () => this.setDevLoginUsers([]),
				});
			},
		});
	}

	private setDevLoginUsers(users: DevLoginUser[]): void {
		this.devLoginUsers = users.filter((user) => user.email);
		this.devLoginAvailable = this.devLoginUsers.length > 0;
	}

	private isLocalHost(): boolean {
		if (typeof window === 'undefined') return false;
		const host = window.location.hostname;
		if (['localhost', '127.0.0.1', '::1'].includes(host)) return true;
		// Tailnet preview: raw IPs (tailscale CGNAT 100.x, LAN 192.168.x/10.x) are
		// never a deployed host — staging/prod are always domain names.
		return (
			/^100\.\d+\.\d+\.\d+$/.test(host) || /^192\.168\.\d+\.\d+$/.test(host) || /^10\.\d+\.\d+\.\d+$/.test(host)
		);
	}

	/**
	 * Checks viewport size and updates input size accordingly.
	 * Three-tier scaling: small for short laptops/tablets, medium for narrow desktops,
	 * large by default. Matches the height-aware SCSS breakpoints below.
	 */
	private checkViewportSize(): void {
		// Check if running in browser environment
		if (typeof window !== 'undefined') {
			const width = window.innerWidth;
			const height = window.innerHeight;

			if (width <= 1024 && height <= 700) {
				this.inputSize = 'small';
			} else if (width <= 1024) {
				this.inputSize = 'medium';
			} else {
				this.inputSize = 'large';
			}
		} else {
			// Default size for SSR
			this.inputSize = 'large';
		}
	}

	/**
	 * Updates form validations based on form type
	 */
	private updateFormValidations(): void {
		switch (this.isRegister) {
			case 1: // Login
				this.authForm = this.fb.group({
					email: ['', [Validators.required, this.validationService.emailValidator()]],
					password: ['', [Validators.required]],
				});
				break;

			case 2: // Reset Password
				this.authForm = this.fb.group({
					email: ['', [Validators.required, this.validationService.emailValidator()]],
				});
				break;

			case 3: // Reset Password Confirm
				this.authForm = this.fb.group({
					newPassword: ['', [Validators.required, this.validationService.passwordValidator()]],
					confirmPassword: [
						'',
						[Validators.required, this.validationService.confirmPasswordValidator('newPassword')],
					],
				});
				break;

			default:
				this.authForm = this.fb.group({}); // Empty by default
		}
	}

	/**
	 * Handles form submission
	 */
	onSubmit(): void {
		if (this.loading) {
			return;
		}

		// Handle MFA submission separately
		if (this.isRegister === 4) {
			this.onMfaSubmit();
			return;
		}

		if (this.authForm.invalid) {
			this.authForm.markAllAsTouched();
			return;
		}

		let formData: any = {};

		switch (this.isRegister) {
			case 1: // Login
			case 2: // Reset Password
				formData = {
					email: this.authForm.get('email')?.value,
					password: this.authForm.get('password')?.value,
				};
				break;
			case 3: // Reset Password Confirm
				formData = {
					newPassword: this.authForm.get('newPassword')?.value,
					confirmPassword: this.authForm.get('confirmPassword')?.value,
				};
				break;
		}

		this.formSubmit.emit(formData);
	}

	/**
	 * Handles MFA form submission
	 */
	onMfaSubmit(): void {
		if (!this.isMfaCodeComplete || this.loading) {
			return;
		}

		const mfaCode = this.mfaCode.join('');

		this.formSubmit.emit({
			mfaCode,
			trustDevice: this.trustDevice,
		});
	}

	/**
	 * Handles MFA cancellation
	 */
	onCancelMFA(): void {
		this.mfaCode = ['', '', '', '', '', ''];
		this.trustDevice = false;
		this.cancelMFA.emit();
	}

	/**
	 * Handles focus on MFA code boxes - selects the content
	 */
	onMfaFocus(event: Event): void {
		const input = event.target as HTMLInputElement;
		input.select();
	}

	/**
	 * Handles input in MFA code boxes
	 */
	onMfaInput(event: Event, index: number): void {
		const input = event.target as HTMLInputElement;
		const value = input.value;

		// Only allow numbers (remove any non-digit characters)
		const numericValue = value.replace(/\D/g, '');

		// Only allow single digit
		if (numericValue.length > 1) {
			input.value = numericValue.charAt(numericValue.length - 1);
		} else {
			input.value = numericValue;
		}

		this.mfaCode[index] = input.value;

		// Auto-focus next input and select its content
		if (input.value && index < 5) {
			const nextInput = input.parentElement?.nextElementSibling?.querySelector('input') as HTMLInputElement;
			if (nextInput) {
				nextInput.focus();
				nextInput.select(); // Select the content so it can be overwritten
			}
		}

		// NO auto-submit, user must click the button
	}

	/**
	 * Handles keydown in MFA code boxes (backspace navigation)
	 */
	onMfaKeydown(event: KeyboardEvent, index: number): void {
		const input = event.target as HTMLInputElement;

		// Handle Enter key to submit
		if (event.key === 'Enter') {
			event.preventDefault();
			const fullMfaCode = this.mfaCode.join('');
			if (fullMfaCode.length === 6) {
				this.onSubmit(); // Call the main submit which handles MFA
			}
			return;
		}

		if (event.key === 'Backspace' && !input.value && index > 0) {
			const prevInput = input.parentElement?.previousElementSibling?.querySelector('input');
			if (prevInput) {
				(prevInput as HTMLInputElement).focus();
			}
		}

		// Handle paste
		if (event.key === 'v' && (event.ctrlKey || event.metaKey)) {
			event.preventDefault();
			navigator.clipboard.readText().then((text) => {
				const digits = text.replace(/\D/g, '').slice(0, 6).split('');
				digits.forEach((digit, i) => {
					if (i < 6) {
						this.mfaCode[i] = digit;
						const inputElement = document.querySelector(`#mfa-${i}`) as HTMLInputElement;
						if (inputElement) {
							inputElement.value = digit;
						}
					}
				});
				// Focus last filled input and select it
				const lastIndex = Math.min(digits.length - 1, 5);
				const lastInput = document.querySelector(`#mfa-${lastIndex}`) as HTMLInputElement;
				if (lastInput) {
					lastInput.focus();
					lastInput.select();
				}
				// NO auto-submit after paste
			});
		}
	}

	/**
	 * Returns error message for a form field
	 */
	getErrorMessage(fieldName: string): string | null {
		const control = this.authForm.get(fieldName);
		if (!control?.errors) return null;

		// Handle specific errors for reset-password-confirm
		if (control.errors['required']) {
			return this.translocoService.translate('auth.errors.required');
		}
		if (control.errors['invalidPassword']) {
			return this.validationService.getValidatorErrorMessage('invalidPassword');
		}
		if (control.errors['passwordsDoNotMatch']) {
			return this.translocoService.translate('auth.errors.passwordsDoNotMatch');
		}

		// Default error handling
		const errorKey = Object.keys(control.errors)[0];
		return errorKey ? this.validationService.getValidatorErrorMessage(errorKey) : null;
	}

	/**
	 * Handles "Enter" key press for form submission
	 */
	submitOnEnter(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.onSubmit();
		}
	}

	submitOnEnterSafe(event: Event): void {
		this.submitOnEnter(event as KeyboardEvent);
	}

	/**
	 * Handles forgot password click based on viewport
	 */
	onForgotPasswordClick(): void {
		if (this.viewportService.isMobileView()) {
			this.showForgotPasswordModal = true;
		} else {
			this.router.navigate(['/reset-password']);
		}
	}

	/**
	 * Handles forgot password form submission
	 */
	onForgotPasswordSubmit(_email: string): void {
		// TODO: Implement password recovery logic
		this.showForgotPasswordModal = false;
	}

	/**
	 * Navigates back to login page
	 */
	navigateToLogin(): void {
		this.router.navigate(['/login']);
	}
}
